import type { Role, UserStatus } from "@scs/contracts";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import type { ApiClient, PublicUser } from "../api/client.js";

const assignableRoles: ReadonlyArray<Exclude<Role, "PLATFORM_ADMIN">> = [
  "ENTERPRISE_ADMIN",
  "SUPERVISOR",
  "COLLECTOR",
  "AUDITOR"
];

export function AdminUsersPage(props: {
  api: ApiClient;
  initialUsers?: readonly PublicUser[];
}): React.JSX.Element {
  const [users, setUsers] = useState<PublicUser[]>([...(props.initialUsers ?? [])]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Exclude<Role, "PLATFORM_ADMIN">>("COLLECTOR");
  const [error, setError] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    try {
      setUsers(await props.api.listUsers());
      setError(null);
    } catch {
      setError("无法加载用户。");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function create(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    try {
      await props.api.createUser({
        email,
        ...(password.length === 0 ? {} : { password }),
        role
      });
      setEmail("");
      setPassword("");
      await refresh();
    } catch {
      setError("创建用户失败。");
    }
  }

  async function updateStatus(user: PublicUser, status: UserStatus): Promise<void> {
    try {
      await props.api.updateUser(user.id, { status });
      await refresh();
    } catch {
      setError("更新用户状态失败。");
    }
  }

  async function revokeSessions(user: PublicUser): Promise<void> {
    try {
      await props.api.revokeUserSessions(user.id);
      await refresh();
    } catch {
      setError("撤销会话失败。");
    }
  }

  return (
    <main>
      <h1>用户管理</h1>
      <form onSubmit={(event) => { void create(event); }}>
        <label>
          邮箱
          <input
            onChange={(event) => { setEmail(event.currentTarget.value); }}
            required
            type="email"
            value={email}
          />
        </label>
        <label>
          初始密码（留空则邀请）
          <input
            minLength={12}
            onChange={(event) => { setPassword(event.currentTarget.value); }}
            type="password"
            value={password}
          />
        </label>
        <label>
          角色
          <select
            onChange={(event) => {
              setRole(event.currentTarget.value as Exclude<Role, "PLATFORM_ADMIN">);
            }}
            value={role}
          >
            {assignableRoles.map((item) => <option key={item}>{item}</option>)}
          </select>
        </label>
        <button type="submit">创建用户</button>
      </form>
      {error === null ? null : <p role="alert">{error}</p>}
      <table>
        <caption>当前租户用户</caption>
        <thead>
          <tr><th>邮箱</th><th>角色</th><th>状态</th><th>操作</th></tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>{user.email}</td>
              <td>{user.role}</td>
              <td>{user.status}</td>
              <td>
                <button
                  onClick={() => { void updateStatus(user, user.status === "DISABLED" ? "ACTIVE" : "DISABLED"); }}
                  type="button"
                >
                  {user.status === "DISABLED" ? "启用" : "禁用"}
                </button>
                <button onClick={() => { void revokeSessions(user); }} type="button">
                  撤销登录会话
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
